import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  NgZone,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../auth/auth.service';

const TICK_MS = 1000;
const WARNING_THRESHOLD_MS = 120_000;
const REFRESH_BEFORE_MS = 60_000;
const ACTIVITY_WINDOW_MS = 60_000;
const MIN_REFRESH_INTERVAL_MS = 30_000;

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'] as const;

@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly auth = inject(AuthService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _now = signal(Date.now());

  private started = false;
  private refreshing = false;
  private loggingOut = false;
  private lastActivityAt = 0;
  private lastRefreshAt = 0;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private readonly activityHandler = () => this.recordActivity();

  readonly remainingMs = computed(() => {
    const exp = this.auth.sessionExpiresAt();
    if (exp === null) return 0;
    return Math.max(0, exp - this._now());
  });

  readonly remainingSeconds = computed(() => Math.floor(this.remainingMs() / 1000));

  readonly isWarning = computed(() => {
    const exp = this.auth.sessionExpiresAt();
    return exp !== null && this.remainingMs() > 0 && this.remainingMs() <= WARNING_THRESHOLD_MS;
  });

  readonly displayTime = computed(() => {
    const total = this.remainingSeconds();
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  });

  constructor() {
    this.destroyRef.onDestroy(() => this.stop());
  }

  start(): void {
    if (!isPlatformBrowser(this.platformId) || this.started) return;
    this.started = true;
    this.recordActivity();
    this.ngZone.runOutsideAngular(() => {
      this.intervalId = setInterval(() => this.ngZone.run(() => this.checkSession()), TICK_MS);
      for (const event of ACTIVITY_EVENTS) {
        document.addEventListener(event, this.activityHandler, { passive: true });
      }
    });
  }

  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (isPlatformBrowser(this.platformId)) {
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, this.activityHandler);
      }
    }
    this.started = false;
  }

  recordActivity(): void {
    this.lastActivityAt = Date.now();
  }

  extend(): void {
    this.recordActivity();
    this.refresh();
  }

  checkSession(): void {
    const now = Date.now();
    this._now.set(now);

    const sessionExp = this.auth.sessionExpiresAt();
    if (sessionExp === null) return;

    if (sessionExp - now <= 0) {
      if (!this.loggingOut) {
        this.loggingOut = true;
        this.auth.logout();
      }
      return;
    }

    this.loggingOut = false;

    const accessExp = this.auth.accessTokenExpiresAt();
    const active = now - this.lastActivityAt < ACTIVITY_WINDOW_MS;
    const throttleOk = now - this.lastRefreshAt > MIN_REFRESH_INTERVAL_MS;
    const accessNearExpiry = accessExp !== null && accessExp - now < REFRESH_BEFORE_MS;

    if (active && throttleOk && accessNearExpiry && !this.refreshing) {
      this.refresh();
    }
  }

  private refresh(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.lastRefreshAt = Date.now();
    this.auth
      .refreshAccessToken()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.refreshing = false;
        },
        error: () => {
          this.refreshing = false;
        },
      });
  }
}
